import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaUserMd,
  FaSyringe,
  FaHeartbeat,
  FaAppleAlt,
  FaDumbbell,
  FaPhone,
  FaArrowRight,
  FaCalendarCheck,
  FaShieldAlt,
} from 'react-icons/fa';

const GLP1Result = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { eligibilityData, isEligible, bmi } = location.state || {};

  useEffect(() => {
    // Redirect if no data
    if (!eligibilityData) {
      navigate('/eligibility');
    }
  }, [eligibilityData, navigate]);

  if (!eligibilityData) {
    return null;
  }

  const renderEligibleResult = () => (
    <div className="max-w-4xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
          <FaCheckCircle className="text-4xl text-green-600" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
          Great News, {eligibilityData.name.split(' ')[0]}!
        </h1>
        <p className="text-xl text-gray-600">
          Based on your assessment, you may be a candidate for our GLP-1 program.
        </p>
      </div>

      {/* BMI Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-navy-50 rounded-xl">
            <p className="text-sm text-gray-600 mb-1">Your BMI</p>
            <p className="text-3xl font-bold text-navy-900">{bmi}</p>
            <p className="text-sm text-gray-500 mt-1">
              {bmi >= 30 ? 'Obese range' : 'Overweight range'}
            </p>
          </div>
          <div className="text-center p-4 bg-gold-50 rounded-xl">
            <p className="text-sm text-gray-600 mb-1">Weight Loss Goal</p>
            <p className="text-3xl font-bold text-gold-600">{eligibilityData.weightLossGoal} kg</p>
            <p className="text-sm text-gray-500 mt-1">Your target</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-xl">
            <p className="text-sm text-gray-600 mb-1">Expected Timeline</p>
            <p className="text-3xl font-bold text-green-600">
              {Math.ceil(parseInt(eligibilityData.weightLossGoal) / 4)}-{Math.ceil(parseInt(eligibilityData.weightLossGoal) / 3)} months
            </p>
            <p className="text-sm text-gray-500 mt-1">At 3-4 kg/month</p>
          </div>
        </div>
      </div>

      {/* What Happens Next */}
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-bold text-navy-900 mb-6">What Happens Next?</h2>
        <div className="space-y-6">
          {[
            {
              step: 1,
              icon: FaCalendarCheck,
              title: 'Doctor Consultation',
              description: 'A licensed physician will review your assessment and schedule a video consultation.',
            },
            {
              step: 2,
              icon: FaSyringe,
              title: 'Prescription & Delivery',
              description: 'If approved, your medication is prescribed and delivered to your doorstep.',
            },
            {
              step: 3,
              icon: FaHeartbeat,
              title: 'Weekly Monitoring',
              description: 'Regular check-ins with your care team to track progress and adjust dosage.',
            },
            {
              step: 4,
              icon: FaAppleAlt,
              title: 'Nutrition & Lifestyle',
              description: 'Personalized diet plans and coaching to maximize your results.',
            },
          ].map(({ step, icon: Icon, title, description }) => (
            <div key={step} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-gold-100 rounded-full flex items-center justify-center">
                <Icon className="text-gold-600 text-xl" />
              </div>
              <div>
                <h3 className="font-semibold text-navy-900">{title}</h3>
                <p className="text-gray-600">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Program Details */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-2xl shadow-lg p-6 sm:p-8 mb-8 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">GLP-1 Weight Loss Program</h2>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <FaCheckCircle className="text-gold-400" />
                FDA-approved GLP-1 medication included
              </li>
              <li className="flex items-center gap-2">
                <FaCheckCircle className="text-gold-400" />
                Doctor consultations & monitoring
              </li>
              <li className="flex items-center gap-2">
                <FaCheckCircle className="text-gold-400" />
                Personalized nutrition & exercise plans
              </li>
              <li className="flex items-center gap-2">
                <FaCheckCircle className="text-gold-400" />
                24/7 support from care team
              </li>
            </ul>
          </div>
          <div className="text-center md:text-right">
            <p className="text-gray-400 text-sm">Starting at</p>
            <p className="text-4xl font-bold text-gold-400">₹24,999</p>
            <p className="text-gray-400 text-sm">/month</p>
          </div>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <Link
          to="/checkout/glp1"
          className="flex-1 flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-600 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors shadow-lg"
        >
          Proceed to Payment
          <FaArrowRight />
        </Link>
        <a
          href="tel:+919999999999"
          className="flex-1 flex items-center justify-center gap-2 border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors"
        >
          <FaPhone />
          Talk to Expert
        </a>
      </div>

      {/* Trust Badge */}
      <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
        <FaShieldAlt className="text-green-600" />
        <span>Your information is secure and protected</span>
      </div>
    </div>
  );

  const renderNotEligibleResult = () => {
    const reason = isEligible?.reason;
    let message = '';
    let recommendation = '';

    switch (reason) {
      case 'pregnancy':
        message = 'GLP-1 medications are not recommended during pregnancy or for those planning to become pregnant.';
        recommendation = 'We recommend our Core lifestyle program which is safe and effective.';
        break;
      case 'eating_disorder':
        message = 'Due to your medical history, GLP-1 may not be the right fit at this time.';
        recommendation = 'Please consult with a healthcare provider before starting any weight loss program.';
        break;
      case 'pancreatitis':
        message = 'GLP-1 medications are contraindicated for those with a history of pancreatitis.';
        recommendation = 'Our Core program offers safe, effective weight loss through nutrition and exercise.';
        break;
      case 'bmi_27_no_condition':
        message = `Your BMI of ${bmi} is in the overweight range, but without certain health conditions, you may not qualify for GLP-1 medication.`;
        recommendation = 'Our Core or Medical Care programs can help you achieve your goals safely.';
        break;
      case 'bmi_under_27':
        message = `Your BMI of ${bmi} indicates you're in a healthy or slightly overweight range.`;
        recommendation = 'Our lifestyle-focused programs are perfect for your goals.';
        break;
      default:
        message = 'Based on your assessment, GLP-1 medication may not be the best option for you right now.';
        recommendation = 'We have other effective programs that might be a better fit.';
    }

    return (
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-6">
            <FaTimesCircle className="text-4xl text-amber-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
            We Have Other Options For You
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {message}
          </p>
        </div>

        {/* Recommendation */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
          <h2 className="text-xl font-bold text-navy-900 mb-4">Our Recommendation</h2>
          <p className="text-gray-600 mb-6">{recommendation}</p>

          {/* Alternative Programs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Core Program */}
            <div className="border-2 border-navy-200 rounded-xl p-6 hover:border-navy-900 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <FaDumbbell className="text-navy-900 text-xl" />
                <h3 className="font-bold text-navy-900">Fitnession Core</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Comprehensive diet and exercise coaching with personalized plans.
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center gap-2 text-sm">
                  <FaCheckCircle className="text-green-500" />
                  Personalized meal plans
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <FaCheckCircle className="text-green-500" />
                  Custom workout routines
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <FaCheckCircle className="text-green-500" />
                  1:1 coaching support
                </li>
              </ul>
              <p className="text-lg font-bold text-navy-900 mb-3">₹4,999/month</p>
              <a
                href="https://fitnession.fit/pricing"
                className="block w-full text-center bg-navy-900 hover:bg-navy-800 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                Learn More
              </a>
            </div>

            {/* Medical Care */}
            <div className="border-2 border-navy-200 rounded-xl p-6 hover:border-navy-900 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <FaUserMd className="text-navy-900 text-xl" />
                <h3 className="font-bold text-navy-900">Medical Care</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Doctor-supervised program with medical monitoring.
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center gap-2 text-sm">
                  <FaCheckCircle className="text-green-500" />
                  Doctor consultations
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <FaCheckCircle className="text-green-500" />
                  Health monitoring
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <FaCheckCircle className="text-green-500" />
                  Personalized plans
                </li>
              </ul>
              <p className="text-lg font-bold text-navy-900 mb-3">₹9,999/month</p>
              <a
                href="https://fitnession.fit/pricing"
                className="block w-full text-center bg-navy-900 hover:bg-navy-800 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>

        {/* Still Want to Talk? */}
        <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 text-center">
          <h3 className="text-xl font-bold text-navy-900 mb-3">Have Questions?</h3>
          <p className="text-gray-600 mb-6">
            Speak with our health experts to find the best program for your needs.
          </p>
          <a
            href="tel:+919999999999"
            className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-white px-8 py-3 rounded-xl font-semibold transition-colors"
          >
            <FaPhone />
            Call Us Now
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4">
        {isEligible?.eligible ? renderEligibleResult() : renderNotEligibleResult()}
      </div>
    </div>
  );
};

export default GLP1Result;
